import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  Camera, Hash, Heart, RefreshCw, Loader2, Plus, Check, X,
  Instagram, ExternalLink, Image, Users, TrendingUp, Award,
  MessageSquare, Share2, Eye, Star
} from 'lucide-react';

export default function UGCCampaignsTab() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([
    {
      id: '1',
      name: 'Share Your Adventure',
      hashtag: '#TourFinderPR',
      description: 'Share your tour photos with our hashtag for a chance to be featured!',
      is_active: true,
      prize: 'Free tour for 2',
      submissions: 45,
      featured: 12,
      created_at: '2026-01-15'
    },
    {
      id: '2',
      name: 'Best Sunset Photo',
      hashtag: '#PRSunset',
      description: 'Capture the most beautiful Puerto Rico sunset',
      is_active: true,
      prize: '$100 tour credit',
      submissions: 23,
      featured: 5,
      created_at: '2026-02-01'
    }
  ]);
  const [submissions, setSubmissions] = useState([]);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    hashtag: '',
    description: '',
    prize: '',
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // In production, load from API
      // const res = await api.get('/admin/ugc/campaigns');
      // setCampaigns(res.data || []);
      
      // Mock submissions
      setSubmissions([
        { id: '1', campaign_id: '1', image_url: 'https://images.unsplash.com/photo-1580065680409-1a92b1a5c155?w=400', username: '@maria_travels', platform: 'instagram', likes: 234, status: 'pending' },
        { id: '2', campaign_id: '1', image_url: 'https://images.unsplash.com/photo-1570789210967-2cac24fe04b0?w=400', username: '@adventure_joe', platform: 'instagram', likes: 567, status: 'featured' },
        { id: '3', campaign_id: '2', image_url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400', username: '@sunset_lover', platform: 'instagram', likes: 892, status: 'pending' },
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async () => {
    if (!newCampaign.name || !newCampaign.hashtag) {
      toast.error('Please fill in required fields');
      return;
    }
    
    const campaign = {
      ...newCampaign,
      id: Date.now().toString(),
      submissions: 0,
      featured: 0,
      created_at: new Date().toISOString().split('T')[0]
    };
    
    setCampaigns([...campaigns, campaign]);
    toast.success('Campaign created!');
    setShowCampaignDialog(false);
    setNewCampaign({ name: '', hashtag: '', description: '', prize: '', is_active: true });
  };

  const toggleCampaignStatus = (campaignId) => {
    setCampaigns(campaigns.map(c => 
      c.id === campaignId ? { ...c, is_active: !c.is_active } : c
    ));
    toast.success('Campaign status updated');
  };

  const updateSubmissionStatus = (submissionId, status) => {
    setSubmissions(submissions.map(s =>
      s.id === submissionId ? { ...s, status } : s
    ));
    toast.success(`Submission ${status === 'featured' ? 'featured' : status === 'rejected' ? 'rejected' : 'updated'}!`);
  };

  // Stats
  const totalSubmissions = submissions.length;
  const featuredCount = submissions.filter(s => s.status === 'featured').length;
  const pendingCount = submissions.filter(s => s.status === 'pending').length;
  const activeCampaigns = campaigns.filter(c => c.is_active).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ugc-campaigns-tab">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Camera className="h-6 w-6 text-pink-500" />
            UGC Campaigns
          </h2>
          <p className="text-muted-foreground">User-generated content and hashtag campaigns</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadData} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={() => setShowCampaignDialog(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-pink-100 flex items-center justify-center">
              <Hash className="h-5 w-5 text-pink-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Campaigns</p>
              <p className="text-xl font-bold text-pink-600">{activeCampaigns}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Image className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Submissions</p>
              <p className="text-xl font-bold text-blue-600">{totalSubmissions}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <Star className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Featured</p>
              <p className="text-xl font-bold text-yellow-600">{featuredCount}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
              <Eye className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pending Review</p>
              <p className="text-xl font-bold text-orange-600">{pendingCount}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="campaigns" className="gap-2">
            <Hash className="h-4 w-4" />
            Campaigns
          </TabsTrigger>
          <TabsTrigger value="submissions" className="gap-2">
            <Image className="h-4 w-4" />
            Submissions
          </TabsTrigger>
          <TabsTrigger value="featured" className="gap-2">
            <Star className="h-4 w-4" />
            Featured
          </TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {campaigns.map((campaign) => (
              <Card key={campaign.id} className={`p-6 ${campaign.is_active ? 'border-pink-200' : 'border-gray-200 opacity-60'}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">{campaign.name}</h3>
                    <Badge variant="outline" className="mt-1 text-pink-600 border-pink-300">
                      <Hash className="h-3 w-3 mr-1" />
                      {campaign.hashtag}
                    </Badge>
                  </div>
                  <Switch
                    checked={campaign.is_active}
                    onCheckedChange={() => toggleCampaignStatus(campaign.id)}
                  />
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">{campaign.description}</p>
                
                {campaign.prize && (
                  <div className="flex items-center gap-2 text-sm mb-4">
                    <Award className="h-4 w-4 text-yellow-500" />
                    <span className="font-medium">Prize: {campaign.prize}</span>
                  </div>
                )}
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Image className="h-4 w-4" />
                    {campaign.submissions} submissions
                  </span>
                  <span className="flex items-center gap-1">
                    <Star className="h-4 w-4" />
                    {campaign.featured} featured
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Submissions Tab */}
        <TabsContent value="submissions" className="mt-4">
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4">Pending Review</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {submissions.filter(s => s.status === 'pending').map((submission) => (
                <div key={submission.id} className="border rounded-lg overflow-hidden">
                  <div className="aspect-square bg-gray-100">
                    <img 
                      src={submission.image_url} 
                      alt="Submission" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Instagram className="h-4 w-4 text-pink-500" />
                      <span className="font-medium text-sm">{submission.username}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                      <Heart className="h-4 w-4" />
                      <span>{submission.likes} likes</span>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => updateSubmissionStatus(submission.id, 'featured')}
                        className="flex-1 gap-1"
                      >
                        <Star className="h-4 w-4" />
                        Feature
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => updateSubmissionStatus(submission.id, 'rejected')}
                        className="text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {submissions.filter(s => s.status === 'pending').length === 0 && (
                <div className="col-span-3 text-center py-12">
                  <Image className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground">No pending submissions</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Featured Tab */}
        <TabsContent value="featured" className="mt-4">
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4">Featured Content</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {submissions.filter(s => s.status === 'featured').map((submission) => (
                <div key={submission.id} className="border rounded-lg overflow-hidden border-yellow-300 bg-yellow-50/50">
                  <div className="aspect-square bg-gray-100 relative">
                    <img 
                      src={submission.image_url} 
                      alt="Featured" 
                      className="w-full h-full object-cover"
                    />
                    <Badge className="absolute top-2 right-2 bg-yellow-500">
                      <Star className="h-3 w-3 mr-1" />
                      Featured
                    </Badge>
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-2">
                      <Instagram className="h-4 w-4 text-pink-500" />
                      <span className="font-medium text-sm">{submission.username}</span>
                    </div>
                  </div>
                </div>
              ))}
              {submissions.filter(s => s.status === 'featured').length === 0 && (
                <div className="col-span-4 text-center py-12">
                  <Star className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground">No featured content yet</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Campaign Dialog */}
      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5 text-pink-500" />
              Create UGC Campaign
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Campaign Name *</Label>
              <Input
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                placeholder="e.g., Share Your Adventure"
              />
            </div>
            
            <div>
              <Label>Hashtag *</Label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={newCampaign.hashtag}
                  onChange={(e) => setNewCampaign({ ...newCampaign, hashtag: e.target.value.replace('#', '') })}
                  placeholder="TourFinderPR"
                  className="pl-9"
                />
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                placeholder="Share your tour photos..."
              />
            </div>
            
            <div>
              <Label>Prize (optional)</Label>
              <Input
                value={newCampaign.prize}
                onChange={(e) => setNewCampaign({ ...newCampaign, prize: e.target.value })}
                placeholder="e.g., Free tour for 2"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">Start collecting submissions immediately</p>
              </div>
              <Switch
                checked={newCampaign.is_active}
                onCheckedChange={(checked) => setNewCampaign({ ...newCampaign, is_active: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignDialog(false)}>Cancel</Button>
            <Button onClick={createCampaign}>Create Campaign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
